import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, categoriesAPI, salesAPI, paymentsAPI, storesAPI, timeclockAPI } from '../lib/api';
import { PinLockScreen } from '../components/PinLockScreen';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
    Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote,
    X, CheckCircle, Loader2, Package, User, AlertCircle, Clock, LogOut,
    LayoutDashboard, Tag, Zap, Printer, ScanLine
} from 'lucide-react';

const TIP_PRESETS = [
    { label: '15%', multiplier: 0.15 },
    { label: '18%', multiplier: 0.18 },
    { label: '20%', multiplier: 0.20 },
    { label: '25%', multiplier: 0.25 },
];

const CASH_DENOMINATIONS = [1, 5, 10, 20, 50, 100];

const CATEGORY_COLORS = [
    'from-cyan-500 to-cyan-600',
    'from-violet-500 to-violet-600',
    'from-emerald-500 to-emerald-600',
    'from-amber-500 to-amber-600',
    'from-rose-500 to-rose-600',
    'from-blue-500 to-blue-600',
    'from-orange-500 to-orange-600',
    'from-pink-500 to-pink-600',
];

function getCategoryColor(index) {
    return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
}

function ShiftTimer({ clockIn }) {
    const [elapsed, setElapsed] = useState('');
    useEffect(() => {
        const update = () => {
            const diff = Date.now() - new Date(clockIn).getTime();
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            setElapsed(`${h}h ${m}m`);
        };
        update();
        const interval = setInterval(update, 60000);
        return () => clearInterval(interval);
    }, [clockIn]);
    return <span className="font-mono text-xs font-bold">{elapsed}</span>;
}

export default function POS() {
    const [searchParamsState] = useSearchParams();
    const { user } = useAuth();
    const {
        items, addItem, removeItem, updateQuantity, clearCart,
        subtotal, stateTax, countyTax, tax, total: cartTotal, discount, setDiscount,
        tip, setTip, itemCount, setTaxRate
    } = useCart();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [lastSale, setLastSale] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [amountPaid, setAmountPaid] = useState('');
    const [processing, setProcessing] = useState(false);
    const [mobileCartOpen, setMobileCartOpen] = useState(false);
    const [selectedTipPreset, setSelectedTipPreset] = useState(null);
    const [customTip, setCustomTip] = useState('');
    const [storeSettings, setStoreSettings] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [discountInput, setDiscountInput] = useState('');
    const navigate = useNavigate();
    const [scannerOpen, setScannerOpen] = useState(false);

    const [requireClockin, setRequireClockin] = useState(false);
    const [clockedIn, setClockedIn] = useState(false);
    const [shiftInfo, setShiftInfo] = useState(null);
    const [clockoutDialogOpen, setClockoutDialogOpen] = useState(false);
    const [clockoutPin, setClockoutPin] = useState('');
    const [clockingOut, setClockingOut] = useState(false);
    const [clockoutSummary, setClockoutSummary] = useState(null);
    const [checkingClockin, setCheckingClockin] = useState(true);

    useEffect(() => {
        const checkClockinRequirement = async () => {
            try {
                const storeId = user?.store_id;
                if (storeId) {
                    const res = await timeclockAPI.getPublicSettings(storeId);
                    const required = res.data?.require_clockin_for_pos || false;
                    setRequireClockin(required);
                    if (required) {
                        const activeRes = await timeclockAPI.getActive();
                        if (activeRes.data?.active) {
                            setClockedIn(true);
                            setShiftInfo({ entryId: activeRes.data.entry.id, clockIn: activeRes.data.entry.clock_in, userName: user?.name });
                        }
                    } else { setClockedIn(true); }
                } else { setClockedIn(true); }
            } catch { setClockedIn(true); }
            finally { setCheckingClockin(false); }
        };
        checkClockinRequirement();
    }, [user]);

    useEffect(() => {
        if (!clockedIn) return;
        if (!user?.store_id) { setLoading(false); return; }
        const fetchData = async () => {
            try {
                const [productsRes, categoriesRes, settingsRes] = await Promise.all([
                    productsAPI.getAll(),
                    categoriesAPI.getAll(),
                    storesAPI.getSettings().catch(() => ({ data: null }))
                ]);
                setProducts(productsRes.data);
                setCategories(categoriesRes.data);
                if (settingsRes.data) {
                    setStoreSettings(settingsRes.data);
                    if (settingsRes.data.tax_rate != null) setTaxRate(settingsRes.data.tax_rate / 100);
                }
            } catch { toast.error('Failed to load products'); }
            finally { setLoading(false); }
        };
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clockedIn]);

    useEffect(() => {
        const payment = searchParams.get('payment');
        const sessionId = searchParams.get('session_id');
        const saleId = localStorage.getItem('pending_stripe_sale_id');
        if (payment === 'success' && sessionId && saleId) {
            localStorage.removeItem('pending_stripe_sale_id');
            setSearchParams({});
            paymentsAPI.stripeConfirm({ session_id: sessionId, sale_id: saleId }).catch(() => {});
            clearCart(); setReceiptOpen(true); toast.success('Payment successful!');
        } else if (payment === 'cancelled') {
            setSearchParams({});
            toast.error('Payment cancelled. Sale saved — retry from sales history.');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isCardPayment = paymentMethod === 'card';
    const cardFee = useMemo(() => {
        if (!isCardPayment || !storeSettings) return 0;
        return (subtotal * (storeSettings.card_fee_percentage || 0) / 100) + (storeSettings.card_fee_flat || 0);
    }, [isCardPayment, storeSettings, subtotal]);

    const showFeeToCustomer = storeSettings?.show_fee_to_customer !== false;
    const total = cartTotal + cardFee;

    const feeDescription = useMemo(() => {
        if (!storeSettings || !isCardPayment) return '';
        const parts = [];
        if (storeSettings.card_fee_percentage > 0) parts.push(`${storeSettings.card_fee_percentage}%`);
        if (storeSettings.card_fee_flat > 0) parts.push(`$${storeSettings.card_fee_flat.toFixed(2)}`);
        return parts.join(' + ');
    }, [storeSettings, isCardPayment]);

    const filteredProducts = products.filter(p => {
        const q = searchQuery.toLowerCase();
        const matchSearch = p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q);
        return matchSearch && (!selectedCategory || p.category_id === selectedCategory) && p.stock_quantity > 0;
    });

    const handleAddToCart = (product) => {
        const cartItem = items.find(i => i.product_id === product.id);
        if (cartItem && cartItem.quantity >= product.stock_quantity) { toast.error('Insufficient stock'); return; }
        addItem(product);
    };

    const handleTipPreset = (preset) => { setSelectedTipPreset(preset.label); setCustomTip(''); setTip(subtotal * preset.multiplier); };
    const handleCustomTip = (v) => { setCustomTip(v); setSelectedTipPreset(null); setTip(parseFloat(v) || 0); };
    const handleNoTip = () => { setSelectedTipPreset('none'); setCustomTip(''); setTip(0); };

    const handleDiscountChange = (v) => {
        setDiscountInput(v);
        const val = parseFloat(v) || 0;
        setDiscount(Math.min(val, subtotal));
    };

    const handleCashQuickSelect = (amount) => {
        setAmountPaid(amount >= total ? amount.toString() : total.toFixed(2));
    };

    const handleCheckout = async () => {
        if (items.length === 0) { toast.error('Cart is empty'); return; }
        setProcessing(true);
        try {
            if (paymentMethod === 'card') {
                const saleData = { items, subtotal, tax, state_tax: stateTax, county_tax: countyTax, tip, card_fee: cardFee, discount, total, payment_method: 'card', amount_paid: total, change_given: 0 };
                const saleRes = await salesAPI.create(saleData);
                const stripeRes = await paymentsAPI.stripeCreateSession({ sale_id: saleRes.data.id, amount: total, success_url: `${window.location.origin}/pos?payment=success&session_id={CHECKOUT_SESSION_ID}`, cancel_url: `${window.location.origin}/pos?payment=cancelled` });
                localStorage.setItem('pending_stripe_sale_id', saleRes.data.id);
                setProcessing(false);
                window.location.href = stripeRes.data.checkout_url;
                return;
            }
            const paid = parseFloat(amountPaid) || total;
            if (paid < total) { toast.error('Insufficient payment amount'); setProcessing(false); return; }
            const response = await salesAPI.create({ items, subtotal, tax, state_tax: stateTax, county_tax: countyTax, tip, card_fee: cardFee, discount, total, payment_method: paymentMethod, amount_paid: paid, change_given: paid - total });
            setLastSale(response.data);
            setCheckoutOpen(false); setReceiptOpen(true); clearCart();
            setAmountPaid(''); setSelectedTipPreset(null); setCustomTip(''); setDiscountInput('');
            toast.success('Sale completed!');
        } catch (error) { toast.error(error.response?.data?.detail || 'Checkout failed'); }
        finally { setProcessing(false); }
    };

    const changeAmount = paymentMethod !== 'stripe' && amountPaid ? parseFloat(amountPaid) - total : 0;

    const handleClockInSuccess = (info) => { setClockedIn(true); setShiftInfo(info); };

    const handleClockOut = async () => {
        if (clockoutPin.length !== 4) { toast.error('Enter your 4-digit PIN'); return; }
        setClockingOut(true);
        try {
            const res = await timeclockAPI.clockOut({ pin: clockoutPin, time_entry_id: shiftInfo.entryId });
            setClockoutSummary(res.data);
            setClockoutDialogOpen(false); setClockoutPin('');
            toast.success('Clocked out successfully!');
            setTimeout(() => { setClockedIn(false); setShiftInfo(null); setClockoutSummary(null); setLoading(true); }, 3000);
        } catch (err) { toast.error(err.response?.data?.detail || 'Clock-out failed'); }
        finally { setClockingOut(false); }
    };

    const getCartQty = (productId) => {
        const item = items.find(i => i.product_id === productId);
        return item ? item.quantity : 0;
    };

    const CartContent = () => (
        <div className="flex flex-col h-full bg-background">
            <div className="px-4 py-3 border-b border-border bg-slate-950 dark:bg-slate-900">
                <div className="flex items-center justify-between">
                    <h2 className="font-black text-sm tracking-tight flex items-center gap-2 text-white" data-testid="cart-title">
                        <ShoppingCart className="h-4 w-4 text-cyan-400" />
                        Current Order
                    </h2>
                    {itemCount > 0 && (
                        <Badge className="bg-cyan-500 text-white font-bold text-xs border-0 rounded-full">
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                        </Badge>
                    )}
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-3 space-y-1.5">
                    {items.length === 0 ? (
                        <div className="text-center py-14">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
                                <ShoppingCart className="h-7 w-7 text-muted-foreground/40" />
                            </div>
                            <p className="text-sm font-bold text-foreground">No items yet</p>
                            <p className="text-xs text-muted-foreground mt-1">Tap a product to start the order</p>
                        </div>
                    ) : items.map((item) => (
                        <div key={item.product_id} className="group rounded-xl bg-muted/40 border border-border/50 p-3 transition-colors hover:bg-muted/70" data-testid={`cart-item-${item.product_id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2.5">
                                <p className="text-sm font-semibold leading-snug flex-1 text-foreground">{item.product_name}</p>
                                <button onClick={() => removeItem(item.product_id)} className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive shrink-0 transition-colors" data-testid={`remove-item-${item.product_id}`}>
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1">
                                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="h-7 w-7 rounded-lg bg-background border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors" data-testid={`decrease-qty-${item.product_id}`}><Minus className="h-3 w-3" /></button>
                                    <span className="w-8 text-center text-sm font-mono font-black text-foreground">{item.quantity}</span>
                                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="h-7 w-7 rounded-lg bg-background border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors" data-testid={`increase-qty-${item.product_id}`}><Plus className="h-3 w-3" /></button>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-sm font-black text-foreground">${item.subtotal.toFixed(2)}</p>
                                    <p className="font-mono text-[10px] text-muted-foreground">${item.unit_price.toFixed(2)} ea</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>

            <div className="border-t border-border bg-background">
                {items.length > 0 && (
                    <div className="px-4 pt-3 pb-2">
                        <div className="flex items-center gap-2">
                            <Tag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <Input
                                type="number"
                                placeholder="Discount ($)"
                                value={discountInput}
                                onChange={(e) => handleDiscountChange(e.target.value)}
                                step="0.01"
                                className="h-8 text-sm"
                                data-testid="inline-discount-input"
                            />
                        </div>
                    </div>
                )}

                <div className="px-4 pb-3 space-y-2">
                    <div className="space-y-1 text-sm pt-1">
                        <div className="flex justify-between text-muted-foreground">
                            <span>Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                                <span>Discount</span><span className="font-mono">-${discount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-muted-foreground">
                            <span>State Tax (4%)</span><span className="font-mono">${stateTax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground">
                            <span>County Tax (4%)</span><span className="font-mono">${countyTax.toFixed(2)}</span>
                        </div>
                        {tip > 0 && (
                            <div className="flex justify-between text-muted-foreground">
                                <span>Tip</span><span className="font-mono">${tip.toFixed(2)}</span>
                            </div>
                        )}
                        {isCardPayment && cardFee > 0 && showFeeToCustomer && (
                            <div className="flex justify-between text-amber-600 dark:text-amber-400">
                                <span>Card Fee {feeDescription && <span className="text-xs opacity-70">({feeDescription})</span>}</span>
                                <span className="font-mono">${cardFee.toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl bg-slate-950 dark:bg-slate-900 p-3 flex items-center justify-between">
                        <span className="text-white font-black text-sm">TOTAL</span>
                        <span className="font-mono font-black text-2xl text-cyan-400">${total.toFixed(2)}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { clearCart(); setDiscountInput(''); }}
                            disabled={items.length === 0}
                            className="h-10 gap-2 text-sm"
                            data-testid="clear-cart-btn"
                        >
                            <Trash2 className="h-4 w-4" /> Clear
                        </Button>
                        <Button
                            onClick={() => setCheckoutOpen(true)}
                            disabled={items.length === 0}
                            className="h-10 bg-cyan-500 hover:bg-cyan-600 text-white font-black text-sm shadow-md shadow-cyan-500/25 gap-1.5"
                            data-testid="checkout-btn"
                        >
                            <Zap className="h-4 w-4" /> Charge
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (checkingClockin) return (
        <div className="h-screen flex items-center justify-center bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
        </div>
    );
    if (requireClockin && !clockedIn) return <PinLockScreen storeId={user?.store_id} onAuthenticated={handleClockInSuccess} />;

    if (clockoutSummary) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-background" data-testid="clockout-summary">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
                    <CheckCircle className="h-10 w-10 text-emerald-500" />
                </div>
                <h2 className="text-3xl font-black mb-1">Shift Complete</h2>
                <p className="text-muted-foreground mb-8">Goodbye, {shiftInfo?.userName}!</p>
                <div className="bg-card rounded-2xl border border-border p-8 space-y-5 text-center w-80 shadow-lg">
                    <p className="text-4xl font-black font-mono text-cyan-500">{clockoutSummary.total_hours ? `${Math.floor(clockoutSummary.total_hours)}h ${Math.round((clockoutSummary.total_hours % 1) * 60)}m` : '—'}</p>
                    <p className="text-sm text-muted-foreground font-semibold uppercase tracking-widest">Shift Duration</p>
                    <Separator />
                    <div className="flex gap-10 justify-center">
                        <div>
                            <p className="font-black text-3xl">{clockoutSummary.session_summary?.sales_count || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1 font-semibold uppercase tracking-wide">Sales</p>
                        </div>
                        <div>
                            <p className="font-black text-3xl">${(clockoutSummary.session_summary?.revenue || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground mt-1 font-semibold uppercase tracking-wide">Revenue</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) return (
        <div className="h-screen flex items-center justify-center bg-background">
            <div className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg">
                    <span className="text-white font-black text-xl">M</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
                    <p className="text-sm text-muted-foreground font-medium">Loading products...</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="h-screen flex overflow-hidden bg-background" data-testid="pos-page">
            <div className="flex-1 flex flex-col min-w-0">

                {/* ── TOP BAR ── */}
                <div className="h-14 bg-slate-950 dark:bg-slate-900 px-4 flex items-center gap-3 shrink-0 border-b border-slate-800">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-800"
                        onClick={() => navigate('/dashboard')}
                        title="Back to Dashboard"
                        data-testid="pos-back-btn"
                    >
                        <LayoutDashboard className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-2.5 shrink-0" data-testid="pos-logo">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-sm">
                            <span className="text-white font-black text-sm">M</span>
                        </div>
                        <div className="hidden sm:block">
                            <p className="font-black text-sm text-white leading-none">Mezgeb POS</p>
                            {storeSettings?.store_name && (
                                <p className="text-[10px] text-slate-400 leading-none mt-0.5">{storeSettings.store_name}</p>
                            )}
                        </div>
                    </div>

                    <div className="relative flex-1 max-w-lg mx-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <Input
                            placeholder="Search products by name, SKU, barcode..."
                            className="pl-9 pr-10 h-9 text-sm bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500/20"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="pos-search"
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-700"
                            onClick={() => navigate('/scan')}
                            title="Scan barcode"
                        >
                            <ScanLine className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="hidden sm:flex items-center gap-2 shrink-0" data-testid="cashier-info">
                        {shiftInfo && (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors border border-emerald-500/20" data-testid="shift-timer-btn">
                                        <Clock className="h-3.5 w-3.5 text-emerald-400" />
                                        <ShiftTimer clockIn={shiftInfo.clockIn} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-52 p-3" align="end">
                                    <p className="font-semibold text-sm">{shiftInfo.userName}</p>
                                    <p className="text-xs text-muted-foreground mb-3">Clocked in: {new Date(shiftInfo.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                    <Button variant="destructive" size="sm" className="w-full" onClick={() => setClockoutDialogOpen(true)} data-testid="clock-out-btn">
                                        <LogOut className="h-4 w-4 mr-2" /> Clock Out
                                    </Button>
                                </PopoverContent>
                            </Popover>
                        )}
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
                            <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center">
                                <span className="text-white text-[10px] font-black">{user?.name?.charAt(0).toUpperCase()}</span>
                            </div>
                            <span className="text-xs text-slate-300 font-semibold truncate max-w-[90px]">{user?.name}</span>
                        </div>
                    </div>

                    <Sheet open={mobileCartOpen} onOpenChange={setMobileCartOpen}>
                        <SheetTrigger asChild>
                            <button className="md:hidden relative h-9 w-9 rounded-lg bg-cyan-500 text-white flex items-center justify-center shrink-0" data-testid="mobile-cart-btn">
                                <ShoppingCart className="h-4 w-4" />
                                {itemCount > 0 && <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{itemCount}</span>}
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-full sm:max-w-sm p-0"><CartContent /></SheetContent>
                    </Sheet>
                </div>

                {/* ── CATEGORY TABS ── */}
                <div className="bg-background border-b border-border shrink-0">
                    <div className="flex gap-1.5 px-4 py-2.5 overflow-x-auto no-scrollbar">
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
                                selectedCategory === null
                                    ? 'bg-slate-950 dark:bg-white text-white dark:text-slate-950 shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                            }`}
                            data-testid="category-all"
                        >
                            All ({products.length})
                        </button>
                        {categories.map((cat, idx) => {
                            const count = products.filter(p => p.category_id === cat.id && p.stock_quantity > 0).length;
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`shrink-0 flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
                                        selectedCategory === cat.id
                                            ? 'bg-cyan-500 text-white shadow-sm shadow-cyan-500/25'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                                    }`}
                                    data-testid={`category-${cat.id}`}
                                >
                                    <span className={`w-4 h-4 rounded-full bg-gradient-to-br ${getCategoryColor(idx)} flex items-center justify-center text-[8px] text-white font-black`}>
                                        {cat.name.charAt(0)}
                                    </span>
                                    {cat.name}
                                    {count > 0 && <span className="text-[10px] opacity-60">({count})</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── PRODUCT GRID ── */}
                <ScrollArea className="flex-1 bg-muted/20">
                    <div className="p-4">
                        {filteredProducts.length === 0 ? (
                            <div className="text-center py-20">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
                                    <Package className="h-7 w-7 text-muted-foreground/40" />
                                </div>
                                {!user?.store_id ? (
                                    <>
                                        <p className="text-sm font-bold text-muted-foreground">No store assigned</p>
                                        <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">Super Admin accounts are not linked to a store. Use the Admin Panel to manage stores.</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-bold text-muted-foreground">No products found</p>
                                        <p className="text-xs text-muted-foreground mt-1">Try a different search or category</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                                {filteredProducts.map((product, idx) => {
                                    const cartQty = getCartQty(product.id);
                                    const isLowStock = product.stock_quantity <= (product.low_stock_threshold || 10);
                                    return (
                                        <button
                                            key={product.id}
                                            onClick={() => handleAddToCart(product)}
                                            className={`group text-left rounded-2xl border overflow-hidden transition-all hover:shadow-lg active:scale-[0.97] relative ${
                                                cartQty > 0
                                                    ? 'border-cyan-500/60 bg-cyan-500/5 shadow-sm shadow-cyan-500/10'
                                                    : 'border-border bg-background hover:border-cyan-500/30'
                                            }`}
                                            data-testid={`product-card-${product.id}`}
                                        >
                                            {cartQty > 0 && (
                                                <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-cyan-500 text-white text-[11px] font-black flex items-center justify-center shadow-md">
                                                    {cartQty}
                                                </div>
                                            )}
                                            {product.image_url ? (
                                                <img src={product.image_url} alt={product.name} className="w-full h-32 object-cover" />
                                            ) : (
                                                <div className={`w-full h-32 bg-gradient-to-br ${getCategoryColor(
                                                    categories.findIndex(c => c.id === product.category_id)
                                                )} opacity-80 flex items-center justify-center`}>
                                                    <span className="text-white font-black text-3xl opacity-90">
                                                        {product.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="p-3">
                                                <p className="text-sm font-bold line-clamp-2 leading-snug mb-2.5 text-foreground group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">{product.name}</p>
                                                <div className="flex items-center justify-between gap-1">
                                                    <p className="font-mono font-black text-base text-foreground">${product.price.toFixed(2)}</p>
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                                        isLowStock
                                                            ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                                                            : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                                    }`}>
                                                        {product.stock_quantity}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </ScrollArea>

                {/* ── MOBILE FLOATING CHARGE BAR ── */}
                {itemCount > 0 && (
                    <div className="md:hidden shrink-0 px-4 py-3 border-t border-border bg-background">
                        <button
                            onClick={() => setCheckoutOpen(true)}
                            className="w-full h-14 bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white font-black text-lg rounded-2xl flex items-center justify-between px-5 shadow-lg shadow-cyan-500/30 transition-colors"
                            data-testid="mobile-charge-btn"
                        >
                            <span className="flex items-center gap-2">
                                <Zap className="h-5 w-5" />
                                Charge
                            </span>
                            <span className="flex items-center gap-3">
                                <span className="text-cyan-200 text-sm font-semibold">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                                <span className="font-mono font-black text-xl">${total.toFixed(2)}</span>
                            </span>
                        </button>
                    </div>
                )}
            </div>

            {/* ── CART PANEL ── */}
            <div className="hidden md:flex w-80 lg:w-[340px] xl:w-96 border-l border-border flex-col shrink-0 bg-background">
                <CartContent />
            </div>

            {/* ── CHECKOUT DIALOG ── */}
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
                <DialogContent className="sm:max-w-md" data-testid="checkout-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-black">Complete Payment</DialogTitle>
                        <DialogDescription>
                            <span className="font-mono font-black text-foreground text-2xl">${total.toFixed(2)}</span>
                            <span className="text-muted-foreground text-sm ml-2">due</span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                            {[{ key: 'cash', label: 'Cash', icon: Banknote }, { key: 'card', label: 'Card / Stripe', icon: CreditCard }].map(({ key, label, icon: Icon }) => (
                                <button
                                    key={key}
                                    onClick={() => setPaymentMethod(key)}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                                        paymentMethod === key
                                            ? 'border-cyan-500 bg-cyan-500/5'
                                            : 'border-border hover:border-muted-foreground/30'
                                    }`}
                                    data-testid={`payment-${key}`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${paymentMethod === key ? 'bg-cyan-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <span className={`text-sm font-bold ${paymentMethod === key ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                                </button>
                            ))}
                        </div>

                        {isCardPayment && cardFee > 0 && showFeeToCustomer && (
                            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="card-fee-notice">
                                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">Card fee: {feeDescription} = <span className="font-mono">${cardFee.toFixed(2)}</span></p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-sm font-bold flex items-center gap-2">Add a Tip <span className="text-xs text-muted-foreground font-normal">(optional)</span></label>
                            <div className="grid grid-cols-5 gap-1.5">
                                {TIP_PRESETS.map((p) => (
                                    <button key={p.label} onClick={() => handleTipPreset(p)} className={`py-2 rounded-lg text-xs font-bold transition-all ${selectedTipPreset === p.label ? 'bg-cyan-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-testid={`tip-${p.label}`}>{p.label}</button>
                                ))}
                                <button onClick={handleNoTip} className={`py-2 rounded-lg text-xs font-bold transition-all ${selectedTipPreset === 'none' ? 'bg-cyan-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-testid="tip-none">None</button>
                            </div>
                            <Input type="number" placeholder="Custom tip $" value={customTip} onChange={(e) => handleCustomTip(e.target.value)} step="0.01" className="h-9 text-sm" data-testid="custom-tip-input" />
                        </div>

                        {paymentMethod === 'cash' && (
                            <div className="space-y-2">
                                <label className="text-sm font-bold">Amount Tendered</label>
                                <div className="grid grid-cols-3 gap-1.5">
                                    <button
                                        onClick={() => setAmountPaid(total.toFixed(2))}
                                        className="py-2 rounded-lg text-xs font-bold bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors"
                                    >
                                        Exact
                                    </button>
                                    {CASH_DENOMINATIONS.filter(d => d >= Math.floor(total)).slice(0, 5).map(d => (
                                        <button
                                            key={d}
                                            onClick={() => handleCashQuickSelect(d)}
                                            className="py-2 rounded-lg text-xs font-bold bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                                        >
                                            ${d}
                                        </button>
                                    ))}
                                </div>
                                <Input
                                    type="number"
                                    placeholder={`$${total.toFixed(2)}`}
                                    value={amountPaid}
                                    onChange={(e) => setAmountPaid(e.target.value)}
                                    step="0.01"
                                    className="h-12 text-xl font-mono font-bold text-center"
                                    data-testid="amount-paid-input"
                                />
                                {changeAmount > 0 && (
                                    <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                                        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Change due</span>
                                        <span className="font-mono font-black text-xl text-emerald-600 dark:text-emerald-400">${changeAmount.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="bg-muted/50 rounded-xl p-3 space-y-1 text-sm">
                            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span></div>
                            {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span className="font-mono">-${discount.toFixed(2)}</span></div>}
                            <div className="flex justify-between text-muted-foreground"><span>Tax</span><span className="font-mono">${tax.toFixed(2)}</span></div>
                            {tip > 0 && <div className="flex justify-between text-muted-foreground"><span>Tip</span><span className="font-mono">${tip.toFixed(2)}</span></div>}
                            {isCardPayment && cardFee > 0 && <div className="flex justify-between text-amber-600"><span>Card Fee</span><span className="font-mono">${cardFee.toFixed(2)}</span></div>}
                            <Separator className="my-1" />
                            <div className="flex justify-between font-black text-base"><span>Total</span><span className="font-mono">${total.toFixed(2)}</span></div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setCheckoutOpen(false)}>Cancel</Button>
                        <Button
                            onClick={handleCheckout}
                            disabled={processing}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white font-black px-6 shadow-md shadow-cyan-500/25"
                            data-testid="confirm-payment-btn"
                        >
                            {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Complete Sale
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── RECEIPT DIALOG ── */}
            <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
                <DialogContent className="sm:max-w-sm p-0 overflow-hidden" data-testid="receipt-dialog">
                    {lastSale && (
                        <div>
                            <div className="bg-gradient-to-br from-slate-950 to-slate-800 text-white text-center py-6 px-4">
                                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg">
                                    <span className="font-black text-xl">M</span>
                                </div>
                                <h3 className="font-black text-lg tracking-widest uppercase">Mezgeb POS</h3>
                                {storeSettings?.store_name && <p className="text-slate-400 text-xs mt-0.5">{storeSettings.store_name}</p>}
                                <p className="text-slate-400 text-xs mt-2">Thank you for your purchase!</p>
                            </div>
                            <div className="p-5 space-y-4 font-mono text-sm">
                                <div className="space-y-2">
                                    {lastSale.items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between">
                                            <span className="font-sans font-medium">{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.product_name}</span>
                                            <span className="font-semibold">${item.subtotal.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                                <Separator className="border-dashed" />
                                <div className="space-y-1">
                                    <div className="flex justify-between text-muted-foreground"><span className="font-sans">Subtotal</span><span>${lastSale.subtotal.toFixed(2)}</span></div>
                                    <div className="flex justify-between text-muted-foreground"><span className="font-sans">Tax</span><span>${((lastSale.state_tax || 0) + (lastSale.county_tax || 0)).toFixed(2)}</span></div>
                                    {(lastSale.tip || 0) > 0 && <div className="flex justify-between text-muted-foreground"><span className="font-sans">Tip</span><span>${lastSale.tip.toFixed(2)}</span></div>}
                                    {(lastSale.card_fee || 0) > 0 && <div className="flex justify-between text-amber-600"><span className="font-sans">Card Fee</span><span>${lastSale.card_fee.toFixed(2)}</span></div>}
                                    {(lastSale.discount || 0) > 0 && <div className="flex justify-between text-emerald-600"><span className="font-sans">Discount</span><span>-${lastSale.discount.toFixed(2)}</span></div>}
                                </div>
                                <Separator className="border-dashed" />
                                <div className="flex justify-between items-baseline">
                                    <span className="font-sans font-black text-lg">Total</span>
                                    <span className="font-black text-2xl text-cyan-600">${lastSale.total.toFixed(2)}</span>
                                </div>
                                <Separator className="border-dashed" />
                                <div className="text-xs text-muted-foreground space-y-0.5">
                                    <div className="flex justify-between"><span className="font-sans uppercase font-semibold">{lastSale.payment_method}</span><span>${lastSale.amount_paid.toFixed(2)}</span></div>
                                    {lastSale.change_given > 0 && <div className="flex justify-between"><span className="font-sans">Change</span><span>${lastSale.change_given.toFixed(2)}</span></div>}
                                    <p className="font-sans mt-2">Cashier: {lastSale.cashier_name}</p>
                                    <p className="font-sans">{new Date(lastSale.created_at).toLocaleString()}</p>
                                    <p className="font-sans">Receipt #: {lastSale.receipt_number}</p>
                                </div>
                            </div>
                            <div className="px-5 pb-5 space-y-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => window.print()} 
                                    className="w-full h-11"
                                >
                                    <Printer className="h-4 w-4 mr-2" /> Print Receipt
                                </Button>
                                <Button onClick={() => setReceiptOpen(false)} className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-white font-black" data-testid="close-receipt-btn">
                                    <CheckCircle className="h-4 w-4 mr-2" /> Done
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── CLOCK-OUT DIALOG ── */}
            <Dialog open={clockoutDialogOpen} onOpenChange={setClockoutDialogOpen}>
                <DialogContent className="sm:max-w-sm" data-testid="clockout-dialog">
                    <DialogHeader>
                        <DialogTitle>Clock Out</DialogTitle>
                        <DialogDescription>Enter your 4-digit PIN to end your shift.</DialogDescription>
                    </DialogHeader>
                    <Input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="••••"
                        value={clockoutPin}
                        onChange={e => setClockoutPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="text-center text-3xl tracking-[0.6em] font-mono h-16"
                        data-testid="clockout-pin-input"
                    />
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => { setClockoutDialogOpen(false); setClockoutPin(''); }}>Cancel</Button>
                        <Button variant="destructive" onClick={handleClockOut} disabled={clockingOut || clockoutPin.length !== 4} data-testid="confirm-clockout-btn">
                            {clockingOut && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Clock Out
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
